import watermarkSrc from "../../../assets/icons/Brand_Logos/ScrollX_LOGO_White_new.png";
import { buildCanvasScene, buildCardCanvasScene } from "./buildScene";
import { imageStore, loadImage } from "./assets";
import {
  paintStripBackground,
  paintWatermark,
  paintWrapperContent,
} from "./paint/paintScene";

const createSurface = (width, height) => {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const toPngBlob = (surface) =>
  typeof surface.convertToBlob === "function"
    ? surface.convertToBlob({ type: "image/png" })
    : new Promise((resolve, reject) =>
        surface.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("PNG encode failed"))),
          "image/png",
        ),
      );

/**
 * Lay out the scene once and rasterize its wrappers on the export pixel
 * grid. Wrapper heights are cumulatively rounded so the strip of PNGs adds
 * up to exactly round(contentHeight × scale), like generateBlockImages.
 *
 * backgroundColor: matte color, or null for alpha formats (transparent and
 * no background image).
 */
export async function prepareCanvasExport({
  credits,
  settings,
  fontStyle,
  targetWidth,
  backgroundColor = null,
  watermark = false,
}) {
  const scene = await buildCanvasScene({ credits, settings, fontStyle, blankHeight: 0 });
  if (!scene.wrappers.length) throw new Error("No credit blocks to export");

  const scale = targetWidth / scene.width;
  const outWidth = Math.max(1, Math.round(targetWidth));
  const backgroundUrl = backgroundColor ? settings?.background_image || "" : "";
  const [backgroundEntry, watermarkEntry] = await Promise.all([
    backgroundUrl ? loadImage(backgroundUrl) : null,
    watermark ? loadImage(watermarkSrc, { crossOrigin: null }) : null,
  ]);
  if (backgroundUrl && !backgroundEntry) {
    console.warn("[canvas export] background image could not be loaded with CORS");
  }

  const rasterize = async (wrapper, { content = true, background = true } = {}) => {
    const top = wrapper.top - scene.contentTop;
    const deviceTop = Math.round(top * scale);
    const deviceBottom = Math.round((top + wrapper.height) * scale);
    const outHeight = Math.max(1, deviceBottom - deviceTop);
    const surface = createSurface(outWidth, outHeight);
    const ctx = surface.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // Scene y → device y, with this wrapper's first pixel row at 0.
    ctx.setTransform(scale, 0, 0, scale, 0, -scene.contentTop * scale - deviceTop);

    if (background && backgroundColor) {
      ctx.save();
      ctx.translate(0, scene.contentTop);
      paintStripBackground(ctx, {
        color: backgroundColor,
        image: backgroundEntry?.image || null,
        width: scene.width,
        height: scene.height,
      });
      ctx.restore();
    }
    if (content) {
      paintWrapperContent(ctx, wrapper, { images: imageStore });
      if (watermarkEntry?.image) {
        ctx.save();
        ctx.translate(0, wrapper.top);
        paintWatermark(ctx, watermarkEntry.image, {
          width: scene.width,
          height: wrapper.height,
        });
        ctx.restore();
      }
    }
    return {
      blob: await toPngBlob(surface),
      width: outWidth,
      height: outHeight,
      cssWidth: scene.width,
      cssHeight: wrapper.height,
    };
  };

  return {
    scene,
    /** Same shape as generateBlockImages: [{ blob, idx, width, height, cssWidth, cssHeight }]. */
    async blocks({ onProgress } = {}) {
      const results = [];
      for (let idx = 0; idx < scene.wrappers.length; idx += 1) {
        const image = await rasterize(scene.wrappers[idx]);
        results.push({ ...image, idx });
        onProgress?.(idx + 1, scene.wrappers.length);
      }
      return results;
    },
    /** Same shape as generateEndHoldAssets: { background, foreground }. */
    async endHold() {
      const last = scene.wrappers[scene.wrappers.length - 1];
      const [background, foreground] = [
        await rasterize(last, { content: false, background: true }),
        await rasterize(last, { content: true, background: false }),
      ];
      return { background, foreground };
    },
  };
}

const paintCoverImage = (ctx, image, width, height) => {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!(sourceWidth > 0) || !(sourceHeight > 0)) return;
  const scale = Math.max(width / sourceWidth, height / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  ctx.drawImage(
    image,
    (width - drawWidth) / 2,
    (height - drawHeight) / 2,
    drawWidth,
    drawHeight,
  );
};

/**
 * Rasterize each card frame at the editor layout size, scaled up to the
 * video resolution. Text wrap stays the preview wrap.
 */
export async function prepareCardCanvasExport({
  credits,
  settings,
  fontStyle,
  resolution,
  targetWidth,
  targetHeight,
  backgroundColor = null,
  watermark = false,
}) {
  const scene = await buildCardCanvasScene({
    credits,
    settings,
    fontStyle,
    resolution,
  });
  if (!scene.wrappers.length) throw new Error("No credit blocks to export");

  const outWidth = Math.max(1, Math.round(targetWidth));
  const outHeight = Math.max(1, Math.round(targetHeight));
  const backgroundUrl = backgroundColor ? settings?.background_image || "" : "";
  const [backgroundEntry, watermarkEntry] = await Promise.all([
    backgroundUrl ? loadImage(backgroundUrl) : null,
    watermark ? loadImage(watermarkSrc, { crossOrigin: null }) : null,
  ]);
  if (backgroundUrl && !backgroundEntry) {
    console.warn("[canvas export] background image could not be loaded with CORS");
  }

  const rasterize = async (wrapper) => {
    const surface = createSurface(outWidth, outHeight);
    const ctx = surface.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.setTransform(
      outWidth / scene.width,
      0,
      0,
      outHeight / scene.frameHeight,
      0,
      0,
    );
    if (backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, scene.width, scene.frameHeight);
      if (backgroundEntry?.image) {
        paintCoverImage(ctx, backgroundEntry.image, scene.width, scene.frameHeight);
      }
    }
    paintWrapperContent(ctx, wrapper, { images: imageStore });
    if (watermarkEntry?.image) {
      paintWatermark(ctx, watermarkEntry.image, {
        width: scene.width,
        height: scene.frameHeight,
      });
    }
    return { blob: await toPngBlob(surface), idx: 0 };
  };

  return {
    scene,
    async blocks({ onProgress } = {}) {
      const results = [];
      for (let idx = 0; idx < scene.wrappers.length; idx += 1) {
        const image = await rasterize(scene.wrappers[idx]);
        results.push({ blob: image.blob, idx });
        onProgress?.(idx + 1, scene.wrappers.length);
      }
      return results;
    },
  };
}

export async function exportCanvasBlocks(options) {
  const exporter = await prepareCanvasExport(options);
  return exporter.blocks(options);
}

export async function exportCanvasEndHold(options) {
  const exporter = await prepareCanvasExport(options);
  return exporter.endHold();
}
